import { useMemo, useReducer } from "react";
import { Link } from "react-router-dom";
import { Box, Divider } from "@mui/material";
import { GlobalButton, GlobalTabCollection, StyledTab } from "../../pages/styles";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { TemplatesTable } from "./TemplatesTable";
import { mySamplesFilterOptions, MySamplesTable } from "./MySamplesTable";
import { FilterWrapper } from "./FilterWrapper";
import { CsvExportPayload, Filter, FilterOption } from "./types";
import { DraftsTable } from "./DraftsTable";
import { exportToCsv } from "../../global";

enum SamplesTab {
  MySamples = "My Samples",
  Drafts = "Drafts",
  Templates = "Templates",
}

interface TabProps {
  filters: Filter[];
  onExportPayloadChange: (payload: CsvExportPayload) => void;
}

interface Tab {
  key: SamplesTab;
  label: SamplesTab;
  Component: React.ComponentType<TabProps>;
  disableFilters?: boolean;
  filterOptions?: FilterOption[];
}

const tabs: Tab[] = [
  {
    key: SamplesTab.MySamples,
    label: SamplesTab.MySamples,
    Component: MySamplesTable,
    disableFilters: false,
    filterOptions: mySamplesFilterOptions,
  },
  {
    key: SamplesTab.Drafts,
    label: SamplesTab.Drafts,
    Component: DraftsTable,
    disableFilters: true,
  },
  {
    key: SamplesTab.Templates,
    label: SamplesTab.Templates,
    Component: TemplatesTable,
    disableFilters: true,
  },
];

interface TabState {
  activeTab: SamplesTab;
  filtersByTab: Record<SamplesTab, Filter[]>;
  exportPayloads: Record<SamplesTab, CsvExportPayload | null>;
}

type TabAction =
  | { type: "SET_ACTIVE_TAB"; tab: SamplesTab }
  | { type: "SET_FILTERS"; tab: SamplesTab; filters: Filter[] }
  | { type: "SET_EXPORT_PAYLOAD"; tab: SamplesTab; payload: CsvExportPayload };

const tabReducer = (state: TabState, action: TabAction): TabState => {
  switch (action.type) {
    case "SET_ACTIVE_TAB":
      return { ...state, activeTab: action.tab };
    case "SET_FILTERS":
      return {
        ...state,
        filtersByTab: { ...state.filtersByTab, [action.tab]: action.filters },
      };
    case "SET_EXPORT_PAYLOAD":
      return {
        ...state,
        exportPayloads: { ...state.exportPayloads, [action.tab]: action.payload },
      };
    default:
      return state;
  }
};

const Samples = () => {
  const initialState: TabState = {
    activeTab: SamplesTab.MySamples,
    filtersByTab: {
      [SamplesTab.MySamples]: [],
      [SamplesTab.Drafts]: [],
      [SamplesTab.Templates]: [],
    },
    exportPayloads: {
      [SamplesTab.MySamples]: null,
      [SamplesTab.Drafts]: null,
      [SamplesTab.Templates]: null,
    },
  };

  const [state, dispatch] = useReducer(tabReducer, initialState);
  const activeTab = tabs.find((t) => t.key === state.activeTab)!;
  const { Component: ActiveComponent } = activeTab;
  const activeTabIndex = useMemo(
    () => tabs.findIndex((t) => t.key === activeTab.key),
    [activeTab.key],
  );
  const activeFilters = state.filtersByTab[state.activeTab];
  const activeExportPayload = state.exportPayloads[state.activeTab];

  const handleApplyFilters = (filters: Filter[]) => {
    dispatch({ type: "SET_FILTERS", tab: state.activeTab, filters });
  };

  const handleExportPayloadUpdate = (payload: CsvExportPayload) => {
    dispatch({ type: "SET_EXPORT_PAYLOAD", tab: state.activeTab, payload });
  };

  const handleChangeTab = (_e: React.SyntheticEvent, key: number) => {
    const nextTab = tabs[key].key;
    dispatch({ type: "SET_ACTIVE_TAB", tab: nextTab });
  };

  const handleExport = () => {
    const exportPayload = state.exportPayloads[activeTab.key];
    if (exportPayload) {
      exportToCsv(exportPayload.objList, exportPayload.fileName);
    }
  };

  return (
    <>
      <span className="pageheader">Samples</span>
      <Divider className="dividerbar" />
      <Box sx={{ bgcolor: "#fff", pt: 3, pb: 1 }} display="flex">
        <GlobalTabCollection
          value={activeTabIndex}
          onChange={handleChangeTab}
          aria-label="sample tabs">
          {tabs.map((tab) => (
            <StyledTab key={tab.key} label={tab.label} />
          ))}
        </GlobalTabCollection>

        <GlobalButton
          style={{ marginTop: "-.8rem" }}
          // @ts-ignore
          component={Link}
          to="/newSample"
          variant="contained">
          Register Sample
        </GlobalButton>

        <Box display="flex" alignItems={"center"} marginLeft="auto" marginTop="-.8rem">
          <FilterWrapper
            filterOptions={activeTab.filterOptions || []}
            onApplyFilters={handleApplyFilters}
            disabled={activeTab.disableFilters ?? true}
          />
          <DownloadButton
            onClick={handleExport}
            disabled={!activeExportPayload || activeExportPayload.disabled}
          />
        </Box>
      </Box>

      <ActiveComponent
        filters={activeFilters}
        onExportPayloadChange={(payload) => handleExportPayloadUpdate(payload)}
      />
    </>
  );
};

export default Samples;

interface DownloadButtonProps {
  onClick: () => void;
  disabled: boolean;
}

const DownloadButton = ({ onClick, disabled }: DownloadButtonProps) => {
  return (
    <FileDownloadIcon
      sx={{ m: 1 }}
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
      }}
      onClick={disabled ? undefined : onClick}
    />
  );
};
